import React, { useState, useRef, useEffect } from 'react';
import { DropdownButtonProps } from '../../../types/DesignSystem/DropdownButton';

const DropdownButton: React.FC<DropdownButtonProps> = ({
  label,
  options,
  onSelect,
  variant = 'primary',
  disabled = false,
  className = '',
  align = 'left',
  icon,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const variantClasses = {
    primary: 'tw-bg-blue-500 hover:tw-bg-blue-600 tw-text-white disabled:tw-bg-grey-300',
    secondary:
      'tw-bg-white tw-border tw-border-grey-300 tw-text-grey-900 hover:tw-bg-grey-50 disabled:tw-bg-grey-100 disabled:tw-text-grey-400',
    tertiary:
      'tw-bg-transparent tw-text-blue-500 hover:tw-bg-blue-50 disabled:tw-text-grey-400 disabled:tw-bg-transparent',
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleSelect = (optionId: string | number) => {
    onSelect?.(optionId);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className={`tw-relative tw-inline-block ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className={`tw-px-4 tw-py-2 tw-rounded tw-font-medium tw-text-sm tw-transition-colors tw-flex tw-items-center tw-gap-2 ${
          variantClasses[variant]
        } ${disabled ? 'tw-cursor-not-allowed' : 'tw-cursor-pointer'}`}
      >
        {icon && <span className="tw-flex-shrink-0">{icon}</span>}
        <span>{label}</span>
        <svg
          className={`tw-w-4 tw-h-4 tw-flex-shrink-0 tw-transition-transform ${
            isOpen ? 'tw-rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 14l-7 7m0 0l-7-7m7 7V3"
          />
        </svg>
      </button>

      {isOpen && (
        <div
          className={`tw-absolute tw-top-full tw-mt-2 tw-bg-white tw-rounded tw-border tw-border-grey-200 tw-shadow-lg tw-z-50 tw-min-w-max ${
            align === 'right' ? 'tw-right-0' : 'tw-left-0'
          }`}
        >
          {options.map((option) => (
            <button
              key={option.id}
              onClick={() => handleSelect(option.id)}
              disabled={option.disabled}
              className={`tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-left tw-transition-colors tw-first:tw-rounded-t tw-last:tw-rounded-b ${
                option.disabled
                  ? 'tw-opacity-50 tw-cursor-not-allowed tw-text-grey-400'
                  : 'hover:tw-bg-blue-50 tw-text-grey-900 tw-cursor-pointer'
              }`}
            >
              {option.icon && <span className="tw-flex-shrink-0">{option.icon}</span>}
              <span className="tw-text-sm">{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default DropdownButton;
